import { useCallback, useState } from 'react'
import { DetailModal } from '@/components/dashboard/detail-modal'
import type {
  ArchiveCampaignCallback,
  CreateCampaignCallbacks,
  DuplicateCampaignCallback,
} from '@/components/shared/types'
import type { OrderItem } from '@/flight-deck-api/services/order-items.service'
import { OrderItemListCard } from './order-item-card/order-item-list-card'
import { OrderItemListCardSkeleton } from './order-item-card/order-item-list-card-skeleton'

type Props = {
  orderItems: OrderItem[]
  isLoading?: boolean
  onDuplicateCampaign: DuplicateCampaignCallback
  onArchiveCampaign: ArchiveCampaignCallback
  isFetching: boolean
  currentPage: number
  pageSize: number
  lastItemRef: (node: HTMLDivElement | null) => void
  expandedRows: Set<number>
  toggleRow: (orderItemId: number) => void
} & CreateCampaignCallbacks

// biome-ignore lint/plugin: RETIRED, do not copy. Convert to a named function export when touching this.
export const OrderItemList: React.FC<Props> = props => {
  const {
    orderItems,
    onDuplicateCampaign,
    onArchiveCampaign,
    isLoading = false,
    isFetching,
    currentPage,
    pageSize,
    lastItemRef,
    expandedRows,
    toggleRow,
  } = props
  const [selectedOrderItem, setSelectedOrderItem] = useState<OrderItem | null>(null)
  const [loadingCampaignId, setLoadingCampaignId] = useState<number | null>(null)

  const handleDuplicateCampaign = useCallback(
    async (campaignId: number, isExperiment?: boolean) => {
      try {
        const result = await onDuplicateCampaign(campaignId, isExperiment)

        if (result) {
          setLoadingCampaignId(campaignId)
        }

        return result
      } finally {
        // Clear loading state after operation completes
        setLoadingCampaignId(null)
      }
    },
    [onDuplicateCampaign],
  )

  const handleArchiveCampaign = useCallback(
    async (campaignId: number) => {
      return await onArchiveCampaign(campaignId)
    },
    [onArchiveCampaign],
  )

  const showInitialLoading = isFetching && currentPage === 0
  const showNextPageLoading = isFetching && currentPage > 0

  return (
    <div className="w-full flex flex-col gap-2">
      {showInitialLoading && <LoadingOrderItemListCards numRows={pageSize} keyPrefix="loading" />}
      {orderItems.map((orderItem, index) => {
        const isExpanded = expandedRows.has(orderItem.orderItemId)
        const { orderItemId } = orderItem
        return (
          <OrderItemListCard
            key={orderItemId}
            orderItem={orderItem}
            isExpanded={isExpanded}
            isVisible={!showInitialLoading}
            animationDelay={index * 50}
            handleSelectOrderItem={() => {
              setSelectedOrderItem(orderItem)
            }}
            handleToggleRow={() => toggleRow(orderItemId)}
            onCreateCampaignV2={props.onCreateCampaignV2}
            onDuplicateCampaign={handleDuplicateCampaign}
            onArchiveCampaign={handleArchiveCampaign}
            isDisabled={isLoading}
            isLoading={loadingCampaignId !== null}
          />
        )
      })}
      {showNextPageLoading && <LoadingOrderItemListCards numRows={pageSize} keyPrefix="fetching" />}
      {selectedOrderItem && (
        <DetailModal
          isLoading={isLoading}
          selectedOrderItem={selectedOrderItem}
          onClose={() => setSelectedOrderItem(null)}
          onCreateCampaignV2={props.onCreateCampaignV2}
          onDuplicateCampaign={handleDuplicateCampaign}
          onArchiveCampaign={handleArchiveCampaign}
          open
        />
      )}
      <div ref={lastItemRef} className="h-5" />{' '}
    </div>
  )
}

interface LoadingOrderItemCardsProps {
  numRows: number
  keyPrefix: string
}

// biome-ignore lint/plugin: RETIRED, do not copy. Convert to a named function export when touching this.
const LoadingOrderItemListCards: React.FC<LoadingOrderItemCardsProps> = ({ numRows, keyPrefix }) => {
  return (
    <>
      {Array.from({ length: numRows }).map((_, index) => (
        <OrderItemListCardSkeleton key={`${keyPrefix}-${index}`} />
      ))}
    </>
  )
}
